import type { PackingRecordBody } from '@/infrastructure/FulfillmentAdminClient';

/**
 * Packing records the server did not take yet (SSD-7587). The label scan is the one moment the packer will not press
 * anything again, so a failed save waits here, on this PC, and is sent again; the server keeps a resent record once.
 */
export const PACKING_PENDING_KEY = 'ff.packing.pendingRecords.v1';

/** How many unsent records the PC keeps. The screen shows the count, so reaching it is never silent. */
export const PENDING_KEEP = 500;

export interface PendingPackingRecord {
  orderId: number;
  body: PackingRecordBody;
  failedAt: string;
  error: string;
  /** The server refused it outright (a 4xx other than a timeout or a rate limit): sent again only by hand. */
  permanent?: boolean;
}

export const readPendingRecords = (): PendingPackingRecord[] => {
  try {
    const raw = window.localStorage.getItem(PACKING_PENDING_KEY);
    const stored: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(stored) ? (stored as PendingPackingRecord[]) : [];
  } catch {
    return [];
  }
};

export const writePendingRecords = (records: readonly PendingPackingRecord[]): void => {
  try {
    window.localStorage.setItem(PACKING_PENDING_KEY, JSON.stringify(records));
  } catch {
    // Storage refused (private window, full quota): the list still lives in this tab's memory until it closes.
  }
};

/** Puts a record at the top, replacing an earlier failure of the same record. */
export const withPendingRecord = (
  records: readonly PendingPackingRecord[],
  entry: PendingPackingRecord,
): PendingPackingRecord[] =>
  [entry, ...records.filter((record) => record.body.recordId !== entry.body.recordId)].slice(0, PENDING_KEEP);

export const withoutPendingRecord = (
  records: readonly PendingPackingRecord[],
  recordId: string,
): PendingPackingRecord[] => records.filter((record) => record.body.recordId !== recordId);
